import {
  Bleed,
  Box,
  Button,
  IconCopy,
  IconFilter,
  IconInfo,
  IconNewWindow,
  IconSettings,
  IconSort,
  Inline,
  Spread,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  TooltipRenderer,
} from 'braid-design-system';
import type { ReactNode } from 'react';

import { ThemedExample } from '#src/components/ThemedExample/ThemedExample.tsx';

const HighlightCell = ({ children }: { children: ReactNode }) => (
  <Bleed space="xxsmall">
    <Box
      background="formAccentSoftActive"
      borderRadius="standard"
      padding="xsmall"
    >
      {children}
    </Box>
  </Bleed>
);

const EtcRow = () => (
  <TableRow>
    <TableCell>
      <Text>…</Text>
    </TableCell>
    <TableCell>
      <Text>…</Text>
    </TableCell>
    <TableCell>
      <Text>…</Text>
    </TableCell>
    <TableCell>
      <Text>…</Text>
    </TableCell>
    <TableCell>
      <Text>…</Text>
    </TableCell>
    <TableCell>
      <Text>…</Text>
    </TableCell>
  </TableRow>
);

export const AdSyncPrefix = () => (
  <ThemedExample>
    <Table label="Job ads">
      <TableHeader>
        <TableRow>
          <TableHeaderCell>
            <Text>Reference</Text>
          </TableHeaderCell>
          <TableHeaderCell>
            <HighlightCell>
              <Text tone="formAccent">Title</Text>
            </HighlightCell>
          </TableHeaderCell>
          <TableHeaderCell>
            <Text>Source</Text>
          </TableHeaderCell>
          <TableHeaderCell>
            <Text>Status</Text>
          </TableHeaderCell>
          <TableHeaderCell>
            <Text>Created</Text>
          </TableHeaderCell>
          <TableHeaderCell>
            <Text>Actions</Text>
          </TableHeaderCell>
        </TableRow>
      </TableHeader>

      <TableBody>
        <TableRow>
          <TableCell>
            <Text>AD-123</Text>
          </TableCell>
          <TableCell>
            <HighlightCell>
              <Text tone="formAccent">(Ad Sync) Señor business analyst</Text>
            </HighlightCell>
          </TableCell>
          <TableCell>
            <Text>SEEK</Text>
          </TableCell>
          <TableCell>
            <Text>Active</Text>
          </TableCell>
          <TableCell>
            <Text>2048-01-01</Text>
          </TableCell>
          <TableCell>
            <Text>…</Text>
          </TableCell>
        </TableRow>

        <EtcRow />
      </TableBody>
    </Table>
  </ThemedExample>
);

export const AdSyncSource = () => (
  <ThemedExample>
    <Table label="Job ads">
      <TableHeader>
        <TableRow>
          <TableHeaderCell>
            <Text>Reference</Text>
          </TableHeaderCell>
          <TableHeaderCell>
            <Text>Title</Text>
          </TableHeaderCell>
          <TableHeaderCell>
            <HighlightCell>
              <Spread align="center" space="small">
                <Text tone="formAccent">Source</Text>

                <Text tone="formAccent">
                  <IconSort /> <IconFilter />
                </Text>
              </Spread>
            </HighlightCell>
          </TableHeaderCell>
          <TableHeaderCell>
            <Text>Status</Text>
          </TableHeaderCell>
          <TableHeaderCell>
            <Text>Created</Text>
          </TableHeaderCell>
          <TableHeaderCell>
            <Text>Actions</Text>
          </TableHeaderCell>
        </TableRow>
      </TableHeader>

      <TableBody>
        <TableRow>
          <TableCell>
            <Text>AD-123</Text>
          </TableCell>
          <TableCell>
            <Text>Señor business analyst</Text>
          </TableCell>
          <TableCell>
            <HighlightCell>
              <Text tone="formAccent">SEEK (Ad Sync)</Text>
            </HighlightCell>
          </TableCell>
          <TableCell>
            <Text>Active</Text>
          </TableCell>
          <TableCell>
            <Text>2048-01-01</Text>
          </TableCell>
          <TableCell>
            <Text>…</Text>
          </TableCell>
        </TableRow>

        <EtcRow />
      </TableBody>
    </Table>
  </ThemedExample>
);

export const AdSyncPosted = () => (
  <ThemedExample>
    <Table label="Job ads">
      <TableHeader>
        <TableRow>
          <TableHeaderCell>
            <Text>Reference</Text>
          </TableHeaderCell>
          <TableHeaderCell>
            <Text>Title</Text>
          </TableHeaderCell>
          <TableHeaderCell>
            <Text>Source</Text>
          </TableHeaderCell>
          <TableHeaderCell>
            <Text>Status</Text>
          </TableHeaderCell>
          <TableHeaderCell>
            <Text>Created</Text>
          </TableHeaderCell>
          <TableHeaderCell>
            <Text>Actions</Text>
          </TableHeaderCell>
        </TableRow>
      </TableHeader>

      <TableBody>
        <TableRow>
          <TableCell>
            <Text>AD-123</Text>
          </TableCell>
          <TableCell>
            <Text>Señor business analyst</Text>
          </TableCell>
          <TableCell>
            <Text>SEEK (Ad Sync)</Text>
          </TableCell>
          <TableCell>
            <HighlightCell>
              <Text tone="formAccent">Active</Text>
            </HighlightCell>
          </TableCell>
          <TableCell>
            <Text>2048-01-01</Text>
          </TableCell>
          <TableCell>
            <HighlightCell>
              <Stack space="xxsmall">
                <Button
                  icon={<IconNewWindow />}
                  size="small"
                  tone="formAccent"
                  variant="soft"
                >
                  View
                </Button>
                <Button
                  icon={<IconSettings />}
                  size="small"
                  tone="formAccent"
                  variant="soft"
                >
                  Manage
                </Button>
              </Stack>
            </HighlightCell>
          </TableCell>
        </TableRow>

        <EtcRow />
      </TableBody>
    </Table>
  </ThemedExample>
);

export const AdSyncNotPosted = () => (
  <ThemedExample>
    <Table label="Job ads">
      <TableHeader>
        <TableRow>
          <TableHeaderCell>
            <Text>Reference</Text>
          </TableHeaderCell>
          <TableHeaderCell>
            <Text>Title</Text>
          </TableHeaderCell>
          <TableHeaderCell>
            <Text>Source</Text>
          </TableHeaderCell>
          <TableHeaderCell>
            <Text>Status</Text>
          </TableHeaderCell>
          <TableHeaderCell>
            <Text>Created</Text>
          </TableHeaderCell>
          <TableHeaderCell>
            <Text>Actions</Text>
          </TableHeaderCell>
        </TableRow>
      </TableHeader>

      <TableBody>
        <TableRow>
          <TableCell>
            <Text>AD-123</Text>
          </TableCell>
          <TableCell>
            <Text>Señor business analyst</Text>
          </TableCell>
          <TableCell>
            <Text>SEEK (Ad Sync)</Text>
          </TableCell>
          <TableCell>
            <HighlightCell>
              <Inline space="small" alignY="center">
                <Text tone="formAccent">Not Posted</Text>
                <TooltipRenderer
                  tooltip={
                    <Text>
                      SEEK provided message containing details about why this
                      position was not posted.
                    </Text>
                  }
                >
                  {({ triggerProps }) => (
                    <Box aria-label="Help" {...triggerProps}>
                      <IconInfo size="xsmall" />
                    </Box>
                  )}
                </TooltipRenderer>
              </Inline>
            </HighlightCell>
          </TableCell>
          <TableCell>
            <Text>2048-01-01</Text>
          </TableCell>
          <TableCell>
            <HighlightCell>
              <Stack space="xxsmall">
                <Button
                  icon={<IconSettings />}
                  size="small"
                  tone="formAccent"
                  variant="soft"
                >
                  Manage
                </Button>
              </Stack>
            </HighlightCell>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell>
            <Text>AD-456</Text>
          </TableCell>
          <TableCell>
            <Text>Software Engineer</Text>
          </TableCell>
          <TableCell>
            <Text>SEEK (Ad Sync)</Text>
          </TableCell>
          <TableCell>
            <HighlightCell>
              <Inline space="small" alignY="center">
                <Text tone="formAccent">Couldn&apos;t Post</Text>
                <TooltipRenderer tooltip={<Text>Contact SEEK</Text>}>
                  {({ triggerProps }) => (
                    <Box aria-label="Help" {...triggerProps}>
                      <IconInfo size="xsmall" />
                    </Box>
                  )}
                </TooltipRenderer>
              </Inline>
            </HighlightCell>
          </TableCell>
          <TableCell>
            <Text>2048-02-01</Text>
          </TableCell>
          <TableCell>
            <HighlightCell>
              <Stack space="xxsmall">
                <Button
                  icon={<IconSettings />}
                  size="small"
                  tone="formAccent"
                  variant="soft"
                >
                  Manage
                </Button>
              </Stack>
            </HighlightCell>
          </TableCell>
        </TableRow>

        <EtcRow />
      </TableBody>
    </Table>
  </ThemedExample>
);

export const AdSyncUpdated = () => (
  <ThemedExample>
    <Table label="Job ads">
      <TableHeader>
        <TableRow>
          <TableHeaderCell>
            <Text>Reference</Text>
          </TableHeaderCell>
          <TableHeaderCell>
            <Text>Title</Text>
          </TableHeaderCell>
          <TableHeaderCell>
            <Text>Source</Text>
          </TableHeaderCell>
          <TableHeaderCell>
            <Text>Status</Text>
          </TableHeaderCell>
          <TableHeaderCell>
            <Text>Created</Text>
          </TableHeaderCell>
          <TableHeaderCell>
            <Text>Actions</Text>
          </TableHeaderCell>
        </TableRow>
      </TableHeader>

      <TableBody>
        <TableRow>
          <TableCell>
            <Text>AD-123</Text>
          </TableCell>
          <TableCell>
            <HighlightCell>
              <Text tone="formAccent">Senior business analyst</Text>
            </HighlightCell>
          </TableCell>
          <TableCell>
            <Text>SEEK (Ad Sync)</Text>
          </TableCell>
          <TableCell>
            <HighlightCell>
              <Text tone="formAccent">Active</Text>
            </HighlightCell>
          </TableCell>
          <TableCell>
            <Text>2048-01-01</Text>
          </TableCell>
          <TableCell>
            <Stack space="xxsmall">
              <Button
                icon={<IconNewWindow />}
                size="small"
                tone="formAccent"
                variant="soft"
              >
                View
              </Button>
              <Button
                icon={<IconSettings />}
                size="small"
                tone="formAccent"
                variant="soft"
              >
                Manage
              </Button>
            </Stack>
          </TableCell>
        </TableRow>

        <EtcRow />
      </TableBody>
    </Table>
  </ThemedExample>
);

export const AdSyncSuspended = () => (
  <ThemedExample>
    <Table label="Job ads">
      <TableHeader>
        <TableRow>
          <TableHeaderCell>
            <Text>Reference</Text>
          </TableHeaderCell>
          <TableHeaderCell>
            <Text>Title</Text>
          </TableHeaderCell>
          <TableHeaderCell>
            <Text>Source</Text>
          </TableHeaderCell>
          <TableHeaderCell>
            <Text>Status</Text>
          </TableHeaderCell>
          <TableHeaderCell>
            <Text>Created</Text>
          </TableHeaderCell>
          <TableHeaderCell>
            <Text>Actions</Text>
          </TableHeaderCell>
        </TableRow>
      </TableHeader>

      <TableBody>
        <TableRow>
          <TableCell>
            <Text>AD-123</Text>
          </TableCell>
          <TableCell>
            <Text>Senior business analyst</Text>
          </TableCell>
          <TableCell>
            <Text>SEEK (Ad Sync)</Text>
          </TableCell>
          <TableCell>
            <HighlightCell>
              <Inline space="small" alignY="center">
                <Text tone="formAccent">Suspended</Text>
                <TooltipRenderer
                  tooltip={<Text>Contact SEEK to resolve this issue.</Text>}
                >
                  {({ triggerProps }) => (
                    <Box aria-label="Help" {...triggerProps}>
                      <IconInfo size="xsmall" />
                    </Box>
                  )}
                </TooltipRenderer>
              </Inline>
            </HighlightCell>
          </TableCell>
          <TableCell>
            <Text>2048-01-01</Text>
          </TableCell>
          <TableCell>
            <Stack space="xxsmall">
              <HighlightCell>
                <Button
                  icon={<IconSettings />}
                  size="small"
                  tone="formAccent"
                  variant="soft"
                >
                  Manage
                </Button>
              </HighlightCell>
            </Stack>
          </TableCell>
        </TableRow>

        <EtcRow />
      </TableBody>
    </Table>
  </ThemedExample>
);

export const AdSyncClosed = () => (
  <ThemedExample>
    <Table label="Job ads">
      <TableHeader>
        <TableRow>
          <TableHeaderCell>
            <Text>Reference</Text>
          </TableHeaderCell>
          <TableHeaderCell>
            <Text>Title</Text>
          </TableHeaderCell>
          <TableHeaderCell>
            <Text>Source</Text>
          </TableHeaderCell>
          <TableHeaderCell>
            <Text>Status</Text>
          </TableHeaderCell>
          <TableHeaderCell>
            <Text>Created</Text>
          </TableHeaderCell>
          <TableHeaderCell>
            <Text>Actions</Text>
          </TableHeaderCell>
        </TableRow>
      </TableHeader>

      <TableBody>
        <TableRow>
          <TableCell>
            <Text>AD-123</Text>
          </TableCell>
          <TableCell>
            <Text>Senior business analyst</Text>
          </TableCell>
          <TableCell>
            <Text>SEEK (Ad Sync)</Text>
          </TableCell>
          <TableCell>
            <HighlightCell>
              <Text tone="formAccent">Expired</Text>
            </HighlightCell>
          </TableCell>
          <TableCell>
            <Text>2048-01-01</Text>
          </TableCell>
          <TableCell>
            <HighlightCell>
              <Button
                icon={<IconCopy />}
                size="small"
                tone="formAccent"
                variant="soft"
              >
                Copy
              </Button>
            </HighlightCell>
          </TableCell>
        </TableRow>

        <EtcRow />
      </TableBody>
    </Table>
  </ThemedExample>
);
